import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import AIService from '../../services/aiService';
import DataTable from '../common/DataTable';
import { Plus, Trash2, ChevronDown, ChevronUp, Wand2, Save, AlertCircle, CheckCircle, Clock, FileText, Upload, Download, ExternalLink, X, Settings2 } from 'lucide-react';
import { ChevronRight, ArrowLeft } from 'lucide-react';

const PRE_RFT_STATUS_LABELS = {
  draft: 'Draft',
  active: 'Active',
  published: 'Published',
  closed: 'Closed',
  awarded: 'Awarded',
};

const PRE_RFT_STATUS_BADGE_STYLES = {
  draft: { bg: '#f8fafc', color: '#475569', dot: '#64748b' },
  active: { bg: '#eef2ff', color: '#4338ca', dot: '#4338ca' },
  published: { bg: '#e0f2fe', color: '#0369a1', dot: '#0284c7' },
  closed: { bg: '#fee2e2', color: '#b91c1c', dot: '#dc2626' },
  awarded: { bg: '#dcfce7', color: '#166534', dot: '#16a34a' },
};

const renderPreRftStatusBadge = (status) => {
  const normalized = String(status || 'draft').toLowerCase();
  const styleConfig = PRE_RFT_STATUS_BADGE_STYLES[normalized] || PRE_RFT_STATUS_BADGE_STYLES.draft;
  const label = PRE_RFT_STATUS_LABELS[normalized] || PRE_RFT_STATUS_LABELS.draft;

  return (
    <span
      className="badge-premium"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 14px',
        borderRadius: '50px',
        fontSize: '12px',
        fontWeight: 600,
        background: styleConfig.bg,
        color: styleConfig.color,
        border: `1px solid ${styleConfig.color}30`,
        boxShadow: `0 2px 4px ${styleConfig.color}15`,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: styleConfig.dot,
          boxShadow: `0 0 0 2px ${styleConfig.dot}30`,
        }}
      />
      {label}
    </span>
  );
};
const PHASES = [
  {
    id: 'identificationOfNeed',
    title: 'Identification of Need',
    subtitle: 'Project Conceptualization',
    icon: '📋',
    color: 'bg-blue-50 border-blue-200',
    headerColor: 'text-blue-700',
    fields: [
      { key: 'problemStatement', label: 'Problem Statement', type: 'textarea' },
      { key: 'proposedSolution', label: 'Proposed Solution', type: 'textarea' },
      { key: 'originSource', label: 'Origin Source', type: 'text', hint: 'e.g., Departmental need, Citizen complaint' },
      { key: 'indicativeCost', label: 'Indicative Cost (₹)', type: 'number' },
      { key: 'expectedBenefits', label: 'Expected Benefits', type: 'textarea' }
    ]
  },
  {
    id: 'budgetInclusion',
    title: 'Budget Inclusion',
    subtitle: 'Plan Approval',
    icon: '💰',
    color: 'bg-green-50 border-green-200',
    headerColor: 'text-green-700',
    fields: [
      { key: 'budgetHead', label: 'Budget Head', type: 'text' },
      { key: 'capitalWorksplanAlignment', label: 'Capital Works Plan Alignment', type: 'textarea' },
      { key: 'cdpAlignment', label: 'City Development Plan Aligned', type: 'checkbox' },
      { key: 'reappropriationRequired', label: 'Reappropriation Required', type: 'checkbox' },
      { key: 'budgetAllocation', label: 'Budget Allocation (₹)', type: 'number' },
      { key: 'approvalDate', label: 'Approval Date', type: 'date' }
    ]
  },
  {
    id: 'adminApproval',
    title: 'Administrative Approval',
    subtitle: 'AA - Official Sanction',
    icon: '✅',
    color: 'bg-purple-50 border-purple-200',
    headerColor: 'text-purple-700',
    fields: [
      { key: 'projectNecessity', label: 'Project Necessity', type: 'textarea' },
      { key: 'fundAvailability', label: 'Funds Available', type: 'checkbox' },
      { key: 'administrativeFeasibility', label: 'Administrative Feasibility', type: 'textarea' },
      { key: 'approvalAuthority', label: 'Approval Authority', type: 'text' },
      { key: 'approvalDate', label: 'AA Date', type: 'date' },
      { key: 'aaOrderReference', label: 'AA Order Reference', type: 'text' }
    ]
  },
  {
    id: 'consultantAppointment',
    title: 'Consultant Appointment',
    subtitle: 'If Applicable',
    icon: '👤',
    color: 'bg-indigo-50 border-indigo-200',
    headerColor: 'text-indigo-700',
    fields: [
      { key: 'consultantRequired', label: 'Consultant Required', type: 'checkbox' },
      { key: 'eoiIssued', label: 'EOI Issued', type: 'checkbox' },
      { key: 'rfqIssued', label: 'RFQ Issued', type: 'checkbox' },
      { key: 'selectedConsultant', label: 'Selected Consultant', type: 'text' },
      { key: 'consultantContractDate', label: 'Contract Date', type: 'date' },
      { key: 'consultantResponsibilities', label: 'Responsibilities', type: 'list' }
    ]
  },
  {
    id: 'dprPreparation',
    title: 'DPR Preparation',
    subtitle: 'Detailed Project Report',
    icon: '📄',
    color: 'bg-red-50 border-red-200',
    headerColor: 'text-red-700',
    fields: [
      { key: 'dprScope', label: 'DPR Scope', type: 'textarea' },
      { key: 'technicalArchitecture', label: 'Technical Architecture', type: 'textarea' },
      { key: 'boqEstimate', label: 'BOQ Estimate (₹)', type: 'number' },
      { key: 'implementationTimeline', label: 'Implementation Timeline', type: 'text' },
      { key: 'riskAssessment', label: 'Risk Assessment', type: 'textarea' },
      { key: 'oAndMModel', label: 'Operations & Maintenance Model', type: 'textarea' },
      { key: 'dprDocument', label: 'DPR Document', type: 'text' },
      { key: 'dprPreparedBy', label: 'DPR Prepared By', type: 'text' }
    ]
  },
  {
    id: 'technicalSanction',
    title: 'Technical Sanction',
    subtitle: 'TS - Engineering Approval',
    icon: '⚙️',
    color: 'bg-indigo-50 border-indigo-200',
    headerColor: 'text-indigo-700',
    fields: [
      { key: 'chiefEngineerReview', label: 'Chief Engineer Review', type: 'textarea' },
      { key: 'complianceWithStandards', label: 'Standards Compliance Verified', type: 'checkbox' },
      { key: 'standardsReference', label: 'Standards Reference', type: 'text' },
      { key: 'costEstimationVerified', label: 'Cost Estimation Verified', type: 'checkbox' },
      { key: 'technicalSanctionDate', label: 'TS Date', type: 'date' },
      { key: 'tsOrderReference', label: 'TS Order Reference', type: 'text' }
    ]
  },
  {
    id: 'clearances',
    title: 'Statutory Clearances',
    subtitle: 'Environmental & Regulatory',
    icon: '🔒',
    color: 'bg-cyan-50 border-cyan-200',
    headerColor: 'text-cyan-700',
    fields: [
      { key: 'environmentClearance', label: 'Environment Clearance', type: 'checkbox' },
      { key: 'environmentClearanceDate', label: 'Clearance Date', type: 'date' },
      { key: 'trafficNoc', label: 'Traffic NOC', type: 'checkbox' },
      { key: 'townPlanningApproval', label: 'Town Planning Approval', type: 'checkbox' },
      { key: 'stateDepartmentConcurrence', label: 'State Department Concurrence', type: 'checkbox' },
      { key: 'otherClearances', label: 'Other Clearances', type: 'list' },
      { key: 'allClearancesObtained', label: 'All Clearances Obtained', type: 'checkbox' },
      { key: 'clearanceNotes', label: 'Notes', type: 'textarea' }
    ]
  },
  {
    id: 'procurementApproval',
    title: 'Procurement Approval',
    subtitle: 'Final Cost & Bidding Strategy',
    icon: '📊',
    color: 'bg-lime-50 border-lime-200',
    headerColor: 'text-lime-700',
    fields: [
      { key: 'procurementStrategy', label: 'Procurement Strategy', type: 'text' },
      { key: 'biddingStage', label: 'Bidding Stage', type: 'text' },
      { key: 'approvalAuthority', label: 'Approval Authority', type: 'text' },
      { key: 'approvalDate', label: 'Approval Date', type: 'date' },
      { key: 'procurementOrderReference', label: 'Procurement Order Reference', type: 'text' },
      { key: 'delegatedLimits', label: 'Delegated Limits', type: 'text' }
    ]
  },
  {
    id: 'rfpDrafting',
    title: 'RFP Drafting',
    subtitle: 'Tender Documents Preparation',
    icon: '📝',
    color: 'bg-pink-50 border-pink-200',
    headerColor: 'text-pink-700',
    fields: [
      { key: 'eligibilityCriteria', label: 'Eligibility Criteria', type: 'textarea' },
      { key: 'technicalEvaluationFramework', label: 'Technical Evaluation Framework', type: 'textarea' },
      { key: 'financialEvaluationMethod', label: 'Financial Evaluation Method', type: 'textarea' },
      { key: 'slaAndPenalties', label: 'SLA & Penalties', type: 'textarea' },
      { key: 'contractConditions', label: 'Contract Conditions', type: 'textarea' },
      { key: 'rfpCompliance', label: 'RFP Compliance Standards', type: 'text' },
      { key: 'rfpReady', label: 'RFP Ready for Publication', type: 'checkbox' }
    ]
  },
  {
    id: 'legalScrutiny',
    title: 'Legal Scrutiny',
    subtitle: 'Pre-RFP Vetting',
    icon: '⚖️',
    color: 'bg-amber-50 border-amber-200',
    headerColor: 'text-amber-700',
    fields: [
      { key: 'legalReviewStatus', label: 'Legal Review Status', type: 'text' },
      { key: 'legalReviewDate', label: 'Legal Review Date', type: 'date' },
      { key: 'vigilanceCheckCompleted', label: 'Vigilance Check Completed', type: 'checkbox' },
      { key: 'vigilanceCheckDate', label: 'Vigilance Check Date', type: 'date' },
      { key: 'complianceVerified', label: 'Compliance Verified', type: 'checkbox' },
      { key: 'legalApprovalDate', label: 'Legal Approval Date', type: 'date' },
      { key: 'rfpApproved', label: 'RFP Approved for Publication', type: 'checkbox' },
      { key: 'legalApprovalReference', label: 'Legal Approval Reference', type: 'text' }
    ]
  }
];

const sampleData = {
  identificationOfNeed: {
    problemStatement:
      "MSMEs face challenges in managing inventory, accounting, procurement, and sales across multiple disconnected systems, leading to inefficiencies and data inconsistencies.",
    proposedSolution:
      "Develop a cloud-based Smart ERP platform integrating inventory, finance, CRM, procurement, and reporting modules.",
    originSource: "Departmental Digital Transformation Initiative",
    indicativeCost: 8500000,
    expectedBenefits:
      "Improved operational efficiency, centralized data management, reduced manual effort, and real-time business insights."
  },

  budgetInclusion: {
    budgetHead: "Digital Infrastructure & Enterprise Applications",
    capitalWorksplanAlignment:
      "Aligned with annual digital modernization and MSME support initiatives.",
    cdpAlignment: true,
    reappropriationRequired: false,
    budgetAllocation: 9000000,
    approvalDate: "2025-01-15"
  },

  adminApproval: {
    projectNecessity:
      "Need to streamline operations and improve decision-making capabilities for MSMEs through digital transformation.",
    fundAvailability: true,
    administrativeFeasibility:
      "Resources, budget, and implementation teams are available for project execution.",
    approvalAuthority: "Municipal Commissioner",
    approvalDate: "2025-02-01",
    aaOrderReference: "AA/ERP/2025/014"
  },

  consultantAppointment: {
    consultantRequired: true,
    eoiIssued: true,
    rfqIssued: true,
    selectedConsultant: "TechNova Consulting Pvt Ltd",
    consultantContractDate: "2025-02-20",
    consultantResponsibilities: [
      "Requirement Gathering",
      "System Architecture Design",
      "Project Management",
      "Quality Assurance"
    ]
  },

  dprPreparation: {
    dprScope:
      "Design, development, deployment, training, and maintenance of Smart ERP platform.",
    technicalArchitecture:
      "MERN Stack application hosted on cloud infrastructure with microservice-based backend architecture.",
    boqEstimate: 8750000,
    implementationTimeline: "12 months",
    riskAssessment:
      "User adoption risk, integration challenges, data migration issues, and cybersecurity concerns.",
    oAndMModel:
      "Annual AMC with dedicated support team and cloud infrastructure maintenance.",
    dprDocument: "DPR_Smart_ERP_v1.2.pdf",
    dprPreparedBy: "TechNova Consulting Pvt Ltd"
  },

  technicalSanction: {
    chiefEngineerReview:
      "Technical design reviewed and found compliant with enterprise architecture standards.",
    complianceWithStandards: true,
    standardsReference: "NIC Cloud Guidelines, ISO 27001, OWASP",
    costEstimationVerified: true,
    technicalSanctionDate: "2025-03-10",
    tsOrderReference: "TS/ERP/2025/027"
  },

  clearances: {
    environmentClearance: false,
    environmentClearanceDate: "",
    trafficNoc: false,
    townPlanningApproval: false,
    stateDepartmentConcurrence: true,
    otherClearances: [
      "IT Department Approval",
      "Data Security Compliance Review"
    ],
    allClearancesObtained: true,
    clearanceNotes:
      "Software project; environmental and traffic clearances not applicable."
  },

  procurementApproval: {
    procurementStrategy: "Open Tender",
    biddingStage: "Two-stage",
    approvalAuthority: "Procurement Committee",
    approvalDate: "2025-03-25",
    procurementOrderReference: "PA/ERP/2025/041",
    delegatedLimits: "Up to ₹1 Crore approved under committee authority"
  },

  rfpDrafting: {
    eligibilityCriteria:
      "Minimum 5 years experience in ERP implementation and 3 similar government projects completed.",
    technicalEvaluationFramework:
      "70% technical score based on experience, methodology, team strength, and product capabilities.",
    financialEvaluationMethod:
      "QCBS (Quality and Cost Based Selection) with 70:30 weightage.",
    slaAndPenalties:
      "99.5% uptime, response time within 4 hours, penalties for SLA breaches.",
    contractConditions:
      "12-month implementation period with 3 years support and maintenance.",
    rfpCompliance:
      "MTPP Act, Government e-Procurement Guidelines",
    rfpReady: true
  },

  legalScrutiny: {
    legalReviewStatus: "Completed",
    legalReviewDate: "2025-04-05",
    vigilanceCheckCompleted: true,
    vigilanceCheckDate: "2025-04-07",
    complianceVerified: true,
    legalApprovalDate: "2025-04-10",
    rfpApproved: true,
    legalApprovalReference: "LEGAL/ERP/2025/019"
  }
};

export default function PreRftWorkflowMunicipal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [activePhase, setActivePhase] = useState('identificationOfNeed');
  const [expandedPhase, setExpandedPhase] = useState('identificationOfNeed');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [fillingPhaseId, setFillingPhaseId] = useState(null); // Tracks which phase is being filled by AI
  const [workflowMode, setWorkflowMode] = useState('manual');
  
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({ title: '', description: '' });
  
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  const autoSaveTimerRef = useRef(null);
  const activeProjectRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const res = await api.get('/sourcing/pre-rft/projects');
      if (res.data) {
        setProjects(res.data.projects || []);
        if (res.data.activeProjectId) {
          setActiveProjectId(res.data.activeProjectId);
        } else if (res.data.projects?.length > 0) {
          setActiveProjectId(res.data.projects[0].id);
        }
      }
    } catch (err) {
      showToast('Failed to load project data', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getActiveProject = () => projects.find((p) => p.id === activeProjectId) || null;
  activeProjectRef.current = getActiveProject();

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectForm.title.trim()) return;
    try {
      const res = await api.post('/sourcing/pre-rft/projects', newProjectForm);
      if (res.data.success) {
        const configProjects = res.data.config?.projects || [];
        setProjects(configProjects);
        setActiveProjectId(res.data.config?.activeProjectId || res.data.project.id);
        setShowNewProjectModal(false);
        setNewProjectForm({ title: '', description: '' });
        showToast('Project created successfully', 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create project', 'error');
    }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      const res = await api.delete(`/sourcing/pre-rft/projects/${projectId}`);
      if (res.data.success) {
        const configProjects = res.data.config?.projects || [];
        setProjects(configProjects);
        if (activeProjectId === projectId) {
          setActiveProjectId(configProjects.length > 0 ? configProjects[0].id : null);
          setExpandedPhase(null);
        }
        showToast('Project deleted', 'success');
      }
    } catch (err) {
      showToast('Failed to delete project', 'error');
    }
  };

  const saveProjectPhase = async (projectId, phaseId, data) => {
    setIsSaving(true);
    try {
      await api.put(`/sourcing/pre-rft/projects/${projectId}/phase/${phaseId}`, { data });
    } catch (err) {
      showToast('Auto-save failed', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (phaseId, key, value) => {
    setProjects((prev) =>
      prev.map((p) => {
        if (p.id === activeProjectId) {
          const updatedPhase = { ...(p[phaseId] || {}), [key]: value };
          return { ...p, [phaseId]: updatedPhase };
        }
        return p;
      })
    );

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      const updatedProj = activeProjectRef.current;
      if (updatedProj) {
        saveProjectPhase(activeProjectId, phaseId, updatedProj[phaseId]);
      }
    }, 1000);
  };

  const handleFillWithAI = async (phaseId) => {
    const proj = getActiveProject();
    if (!proj) return;
    
    setFillingPhaseId(phaseId);
    try {
      const contextMsg = `Project Title: ${proj.title}\nDescription: ${proj.description}\n${proj.extractedText ? `Source Document Content:\n${proj.extractedText.substring(0, 8000)}` : ''}\n`;
      const phaseDef = PHASES.find(p => p.id === phaseId);
      
      const prompt = `You are a municipal procurement expert specializing in complex infrastructure projects in India. 
      Your goal is to generate INCREDIBLY DETAILED, PROFESSIONAL, and COMPREHENSIVE content for the "${phaseDef.title}" phase of a major RFP.
      
      The source text provided is from a draft or similar project. Extract every possible detail.
      
      CRITICAL INSTRUCTIONS:
      1. CONTENT LENGTH: For each text/textarea field, provide at least 250-400 words of professional, technically sound content.
      2. DYNAMIC FIELDS: If you identify relevant information in the source document that doesn't fit the standard keys, ADD NEW KEYS to the JSON object. These keys will be dynamically added to our UI.
      3. TONE: Use formal, administrative language suitable for a 30+ page official municipal document.
      
      Context: ${contextMsg}
      
      Return a clean JSON object. Start with these REQUIRED keys: ${phaseDef.fields.map(f => f.key).join(', ')}
      Then add any OTHER relevant keys you discovered.
      
      Rules:
      - Use professional government procurement terminology (e.g., "Schedule of Rates", "Technical Sanction", "Administrative Sanction", "CDP Alignment").
      - If details are missing, use your expertise to synthesize realistic, high-quality professional content.
      - Return ONLY raw JSON text. No markdown fences.`;

      const aiRes = await api.post('/ai/gemini', { message: prompt });
      let aiText = aiRes.data?.response || '';
      
      let parsed = {};
      try {
        const jsonMatch = aiText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const rawParsed = JSON.parse(jsonMatch[0]);
          // STRICT FILTERING: Only accept keys that belong to this phase 
          // or are newly discovered dynamic keys (but not other phase IDs)
          const validKeys = phaseDef.fields.map(f => f.key);
          const otherPhaseIds = PHASES.map(p => p.id).filter(id => id !== phaseId);
          
          Object.keys(rawParsed).forEach(key => {
            // SURGICAL FILTER: Only allow keys for THIS phase
            if (validKeys.includes(key) || (!otherPhaseIds.includes(key) && !['_id', 'id'].includes(key))) {
              parsed[key] = rawParsed[key];
            }
          });
          console.log(`[AI:SurgicalExtraction] Data for Phase ${phaseId}:`, parsed);
        } else {
          throw new Error('No JSON found');
        }
      } catch (parseErr) {
        console.error('Failed to parse AI JSON:', aiText);
        parsed = phaseDef.fields.reduce((acc, field) => {
          if (field.type === 'checkbox') acc[field.key] = true;
          else if (field.type === 'number') acc[field.key] = 50000;
          else acc[field.key] = `AI Suggested ${field.label}`;
          return acc;
        }, {});
      }

      // Update state IMMEDIATELY for snappy UI
      setProjects((prev) => {
        const newProjects = prev.map((p) => {
          if (p.id === activeProjectId) {
            return { ...p, [phaseId]: { ...(p[phaseId] || {}), ...parsed } };
          }
          return p;
        });
        return newProjects;
      });
      
      // Save in background
      saveProjectPhase(activeProjectId, phaseId, { ...(proj[phaseId] || {}), ...parsed });
      showToast(`PHASE ISOLATED: Generated content for "${phaseDef.title}" only.`, 'success');
    } catch (err) {
      showToast('Failed to generate AI suggestions', 'error');
    } finally {
      setFillingPhaseId(null);
    }
  };

  const handleAddCustomField = (phaseId) => {
    const fieldName = window.prompt("Enter the name for the new field:");
    if (!fieldName) return;
    
    const key = fieldName.toLowerCase().replace(/\s+/g, '');
    handleFieldChange(phaseId, key, "");
  };

  const handleFillAllWithAI = async () => {
    if (!activeProjectId) return;
    if (!window.confirm("This will call AI for all 10 phases to generate a detailed RFP. This may take a minute. Continue?")) return;
    
    // We'll use a local state or just handle it sequentially
    try {
      for (const phase of PHASES) {
        showToast(`Generating detailed content for ${phase.title}...`, 'info');
        await handleFillWithAI(phase.id);
      }
      showToast("All phases populated with incredible detail!", "success");
    } catch (err) {
      showToast("Failed to complete full AI generation", "error");
    }
  };

  const handleLoadSampleData = async () => {
    if (!activeProjectId) {
      showToast('Please select a project first', 'warning');
      return;
    }
    if (!window.confirm('This will overwrite all existing phase data with sample data. Continue?')) return;
    
    const transformedData = {};
    
    PHASES.forEach(phase => {
      const phaseSample = sampleData[phase.id];
      if (!phaseSample) return;
      
      const phaseData = { ...phaseSample };
      phase.fields.forEach(field => {
        if (field.type === 'list' && Array.isArray(phaseData[field.key])) {
          phaseData[field.key] = phaseData[field.key].join('\n');
        }
      });
      transformedData[phase.id] = phaseData;
    });
    
    setProjects(prev =>
      prev.map(p => {
        if (p.id === activeProjectId) {
          return { ...p, ...transformedData };
        }
        return p;
      })
    );
    
    for (const [phaseId, data] of Object.entries(transformedData)) {
      try {
        await saveProjectPhase(activeProjectId, phaseId, data);
      } catch (err) {
        console.error(`Failed to save phase ${phaseId}:`, err);
      }
    }
    
    showToast('Sample data loaded successfully!', 'success');
  };

  const handleFileUpload = async (event, projectId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    
    if (activeProjectId !== projectId) {
      await api.put('/sourcing/pre-rft/active', { projectId }).catch(() => {});
      setActiveProjectId(projectId);
    }

    try {
      const res = await api.post('/sourcing/pre-rft/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        showToast(`${file.name} uploaded and processed!`, 'success');
        loadData();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to upload document', 'error');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const generateRftDocument = async () => {
    if (!activeProjectId) return;
    
    // Check if we need to fill phases first
    const completed = getPhaseProgress(activeProject);
    if (completed < 10) {
      const fillAll = window.confirm(`Only ${completed}/10 phases are started. Would you like to use AI to generate incredibly detailed content for all phases before creating the final document?`);
      if (fillAll) {
        await handleFillAllWithAI();
      }
    }

    setIsGeneratingDocument(true);
    try {
      const res = await api.post(`/sourcing/pre-rft/projects/${activeProjectId}/generate-document`);
      if (res.data.success) {
        setGeneratedDocument(res.data.document);
        setShowDocumentModal(true);
        showToast('RFP Document generated successfully', 'success');
      }
    } catch (err) {
      showToast('Failed to generate RFP document', 'error');
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  const getPhaseProgress = (project) => {
    if (!project) return 0;
    let completed = 0;
    PHASES.forEach(phase => {
      const phaseData = project[phase.id] || {};
      const filledFields = Object.keys(phaseData).filter(k => phaseData[k] !== '' && phaseData[k] !== null).length;
      if (filledFields > 0) completed++;
    });
    return completed;
  };

  const openProject = useCallback((proj) => {
    const completed = getPhaseProgress(proj);
    const nextPhase = PHASES[Math.min(completed, PHASES.length - 1)].id;
    navigate(`/sourcing/pre-rft/project/${proj.id}?phase=${nextPhase}`);
  }, [navigate]);

  const tableData = useMemo(
    () => projects.map((project) => ({
      ...project,
      phaseProgress: getPhaseProgress(project),
    })),
    [projects],
  );

  const projectColumns = useMemo(
    () => [
      {
        key: 'title',
        label: 'Project Name',
        width: '35%',
        render: (_, row) => (
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, maxWidth: '360px' }}>
            <span
              title={row.title || ''}
              style={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {row.title || '-'}
            </span>
            {row.id === activeProjectId && (
              <span
                className="badge bg-primary-subtle text-primary border border-primary-subtle"
                style={{ fontSize: '10px', flexShrink: 0 }}
              >
                Active
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        width: '140px',
        render: (_, row) => renderPreRftStatusBadge(row.status),
      },
      {
        key: 'phaseProgress',
        label: 'Progress',
        width: '220px',
        render: (_, row) => {
          const completed = row.phaseProgress ?? 0;
          return (
            <div className="d-flex align-items-center gap-2" style={{ minWidth: '160px' }}>
              <div
                style={{
                  flex: 1,
                  height: 6,
                  background: '#f3f4f6',
                  borderRadius: '9999px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${(completed / 10) * 100}%`,
                    height: '100%',
                    background: '#6366f1',
                    borderRadius: '9999px',
                  }}
                />
              </div>
              <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                {completed}/10
              </span>
            </div>
          );
        },
      },
      {
        key: 'createdAt',
        label: 'Created',
        width: '120px',
        render: (_, row) =>
          row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-',
      },
      {
        key: '_open',
        label: '',
        width: '48px',
        render: () => <ChevronRight size={16} style={{ color: '#9ca3af' }} />,
      },
    ],
    [activeProjectId],
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-[#F6FAFF]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const activeProject = getActiveProject();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
  // Derive server base url by replacing /api/v1
  const serverBaseUrl = apiBaseUrl.replace('/api/v1', '');

  return (
    <div className="flex flex-col h-full bg-[#F6FAFF] font-sans">
      <div className="bg-white border-b border-gray-200 px-6 py-2">
        <div className="flex items-center gap-4 mb-6 text-gray-500">
  <button
    onClick={() => navigate('/sourcing')}
    className="w-8 h-8 border border-gray-200 flex items-center justify-center hover:bg-gray-50"
  >
    <ArrowLeft size={15} />
  </button>

  <button
  onClick={() => navigate('/overview')}
  className="text-sm hover:text-indigo-600 transition-colors"
>
  Dashboard
</button>

  <ChevronRight size={16} />

  <span className="text-sm font-medium text-gray-700">
    Pre-RFP Workflow
  </span>
</div>
<div className="border-b border-gray-200 -mx-6 -mt-3 mb-4"></div>
  <div className="flex items-center justify-between">

    <div>
      <h1 className="text-[20px] font-bold text-gray-900">
        Pre-RFP Workflow
      </h1>

      <p className="mt-2 text-gray-500 text-base">
        Municipal 10-Phase Document Preparation & Approval
      </p>
    </div>

    <div className="flex gap-3">

  <button
    onClick={() => setWorkflowMode('manual')}
    className={`h-[48px] px-8 border text-sm font-medium transition-all ${
      workflowMode === 'manual'
        ? 'bg-[#7886E8] text-white border-[#7886E8]'
        : 'bg-white text-[#7886E8] border-[#7886E8]'
    }`}
  >
    Manual Mode
  </button>

  <button
    onClick={() => setWorkflowMode('automatic')}
    className={`h-[48px] px-8 border text-sm font-medium transition-all ${
      workflowMode === 'automatic'
        ? 'bg-[#7886E8] text-white border-[#7886E8]'
        : 'bg-white text-[#7886E8] border-[#7886E8]'
    }`}
  >
    Automatic Mode
  </button>

</div>

  </div>
</div>

      <div className="flex-1 px-4 pt-3 pb-6 overflow-y-auto">
        <div className="mb-6">
          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              onClick={() => setShowNewProjectModal(true)}
              className="btn btn-primary d-flex align-items-center gap-2"
            >
              <Plus size={16} />
              New Project
            </button>
          </div>

          <DataTable
            data={tableData}
            columns={projectColumns}
            title="Projects"
            loading={false}
            showActions={false}
            searchable
            sortable
            exportable={false}
            showFilterButton={false}
            pagination
            pageSize={10}
            onRowClick={openProject}
            searchPlaceholder="Search projects by name or status..."
            emptyMessage={
              <div className="text-center py-4">
                <FileText size={40} className="text-muted opacity-50 mb-3" />
                <h5 className="text-dark fw-bold mb-2">No projects yet</h5>
                <p className="text-muted mb-0">
                  Create a project to start the 10-phase Pre-RFP workflow.
                </p>
              </div>
            }
            className="shadow-sm border-0"
          />
        </div>
      </div>

      {showNewProjectModal &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            style={{ zIndex: 1100 }}
            onClick={() => setShowNewProjectModal(false)}
            role="presentation"
          >
            <div
              className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="new-project-modal-title"
            >
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <h2 id="new-project-modal-title" className="text-lg font-bold text-gray-900">Create New Project</h2>
                <button type="button" onClick={() => setShowNewProjectModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <form onSubmit={handleCreateProject} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
                    <input type="text" required value={newProjectForm.title} onChange={e => setNewProjectForm({...newProjectForm, title: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea value={newProjectForm.description} onChange={e => setNewProjectForm({...newProjectForm, description: e.target.value})} className="w-full px-4 py-2 border rounded-lg h-24" />
                  </div>
                </div>
                <div className="mt-8 flex gap-3 justify-end">
                  <button type="button" onClick={() => setShowNewProjectModal(false)} className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-50 rounded-lg">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700">Create</button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {showDocumentModal &&
        generatedDocument &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            style={{ zIndex: 1100 }}
            onClick={() => setShowDocumentModal(false)}
            role="presentation"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-full max-h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="rft-document-modal-title"
            >
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                    <FileText size={20} />
                  </div>
                  <div>
                    <h2 id="rft-document-modal-title" className="text-lg font-bold text-gray-900">Generated RFT Document</h2>
                    <p className="text-xs text-gray-500">Review the AI-generated Request for Proposal based on your phase data.</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const blob = new Blob([generatedDocument], { type: 'text/markdown' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `RFT_${activeProject?.title.replace(/\s+/g, '_')}.md`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium shadow-sm"
                  >
                    <Download size={16} />
                    Download
                  </button>
                  <button type="button" onClick={() => setShowDocumentModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
                <div className="max-w-4xl mx-auto bg-white p-10 rounded-xl shadow-sm border border-gray-200 prose prose-indigo max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-gray-800 text-sm leading-relaxed" style={{ fontFamily: 'inherit' }}>
                    {generatedDocument}
                  </pre>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

